import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { RADashboardTheme } from '@/interfaces/raDashboard.interface'
import React from 'react'

// Soft-lock on how many days' worth of Attribution reports can be generated in one go.
export const MAX_ATTRIBUTION_RUN_DAYS = 30

interface ReportCheckDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  message: string
  theme: RADashboardTheme
  dashboardType: FADashboardType
  // Days that would need a GFPA report generated if the user hits Run Report. null when
  // there's nothing runnable (Risk doesn't use this at all; Attribution leaves it null when
  // the range isn't coverable by generation, e.g. missing underlying portfolio data).
  attributionRunnableDates: string[] | null
  // Days in the selected Attribution range that already have a report. null/empty means none
  // of the range has any data yet, so there's nothing to view — only offer "View Anyway" when
  // this is non-empty.
  attributionViewAnywayDates: string[] | null
  onGenerateReport: () => void
  onViewAnyway: () => void
}

export const ReportCheckDialog: React.FC<ReportCheckDialogProps> = ({
  open,
  onOpenChange,
  message,
  theme,
  dashboardType,
  attributionRunnableDates,
  attributionViewAnywayDates,
  onGenerateReport,
  onViewAnyway,
}) => {
  if (!open) return null

  const isAttribution = dashboardType === FADashboardType.Attribution
  const runnableDayCount = attributionRunnableDates?.length ?? 0
  // Risk always has something to run; Attribution only offers the button at all when there's a
  // generatable gap (a range with no coverable days at all — e.g. missing portfolio data — has
  // nothing for Run Report to do, so it stays hidden rather than disabled).
  const showRunButton = !isAttribution || runnableDayCount > 0
  const runButtonDisabled = isAttribution && runnableDayCount > MAX_ATTRIBUTION_RUN_DAYS

  const showViewAnywayButton =
    dashboardType === FADashboardType.Attribution &&
    attributionViewAnywayDates != null &&
    attributionViewAnywayDates.length > 0

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: '8px',
          padding: '32px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: `'0 8px 32px ${theme.benchmarkBoxShadowColor}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '700',
              color: theme.textPrimary,
            }}
          >
            Report Availability Check
          </h3>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.textSecondary,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.inputBackground
              e.currentTarget.style.color = theme.textPrimary
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = theme.textSecondary
            }}
            title='Close dialog'
          >
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <line x1='18' y1='6' x2='6' y2='18'></line>
              <line x1='6' y1='6' x2='18' y2='18'></line>
            </svg>
          </button>
        </div>

        <p
          style={{
            margin: '0 0 24px 0',
            fontSize: '16px',
            color: theme.textSecondary,
            lineHeight: '1.5',
          }}
        >
          {message}
        </p>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={() => onOpenChange(false)}
            style={{
              padding: '12px 20px',
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              backgroundColor: theme.cardBackground,
              color: theme.textPrimary,
              cursor: 'pointer',
            }}
          >
            Change Selection
          </button>

          {showViewAnywayButton && (
            <button
              onClick={onViewAnyway}
              style={{
                padding: '12px 20px',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: theme.cardBackground,
                color: theme.textPrimary,
                cursor: 'pointer',
              }}
            >
              View Anyway
            </button>
          )}

          {showRunButton && (
            <button
              onClick={onGenerateReport}
              disabled={runButtonDisabled}
              title={
                runButtonDisabled
                  ? `Not currently allowed to run more than ${MAX_ATTRIBUTION_RUN_DAYS} days at once.`
                  : undefined
              }
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: theme.runButtonBackground,
                color: theme.runButtonColor,
                cursor: runButtonDisabled ? 'not-allowed' : 'pointer',
                opacity: runButtonDisabled ? 0.5 : 1,
              }}
            >
              Run Report
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
